import { Logger } from "@ng-mw/shared-react-components"

import { performMiddagstipsBffRequest } from "../api/MiddagstipsBff"
import { DinnerSuggestionsImportantToMeOptionId, DinnerSuggestionsMoreOfOptionId } from "./types"

export type DinnerSuggestionsPreferenceName =
    | "BARNEVENNLIG"
    | "ENKEL"
    | "SPARE_TID"
    | "SUNNERE"
    | "FISK"
    | "KJØTT"
    | "KYLLING"
    | "VEGETAR"

export interface DinnerSuggestionsPreferenceRequestItem {
    preferenceName: DinnerSuggestionsPreferenceName
    selected: boolean
}

export interface DinnerSuggestionsPreferenceResponseItem {
    preferenceName: DinnerSuggestionsPreferenceName
    selected: boolean
}

export interface DinnerSuggestionsSelectedPreferences {
    moreOf: DinnerSuggestionsMoreOfOptionId[]
    importantToMe: DinnerSuggestionsImportantToMeOptionId[]
}

type DinnerSuggestionsPreferenceOptionId =
    | DinnerSuggestionsMoreOfOptionId
    | DinnerSuggestionsImportantToMeOptionId

const dinnerSuggestionsPreferenceMappings: {
    preferenceName: DinnerSuggestionsPreferenceName
    optionId: DinnerSuggestionsPreferenceOptionId
}[] = [
    { preferenceName: "BARNEVENNLIG", optionId: "family-friendly" },
    { preferenceName: "ENKEL", optionId: "simple" },
    { preferenceName: "SPARE_TID", optionId: "save-time" },
    { preferenceName: "SUNNERE", optionId: "healthier" },
    { preferenceName: "FISK", optionId: "fish" },
    { preferenceName: "KJØTT", optionId: "meat" },
    { preferenceName: "KYLLING", optionId: "chicken" },
    { preferenceName: "VEGETAR", optionId: "vegetarian" },
]

const dinnerSuggestionsMoreOfOptionIds = new Set<DinnerSuggestionsPreferenceOptionId>([
    "fish",
    "vegetarian",
    "chicken",
    "meat",
])

export const buildDinnerSuggestionsPreferencesRequest = ({
    moreOf,
    importantToMe,
}: DinnerSuggestionsSelectedPreferences): DinnerSuggestionsPreferenceRequestItem[] => {
    const selectedPreferenceIds = new Set<DinnerSuggestionsPreferenceOptionId>([
        ...moreOf,
        ...importantToMe,
    ])

    return dinnerSuggestionsPreferenceMappings.map(({ preferenceName, optionId }) => ({
        preferenceName,
        selected: selectedPreferenceIds.has(optionId),
    }))
}

export const parseDinnerSuggestionsPreferencesResponse = (
    preferences: DinnerSuggestionsPreferenceResponseItem[],
): DinnerSuggestionsSelectedPreferences => {
    const selectedPreferenceNames = new Set(
        preferences
            .filter(({ selected }) => selected)
            .map(({ preferenceName }) => preferenceName),
    )

    return dinnerSuggestionsPreferenceMappings.reduce<DinnerSuggestionsSelectedPreferences>(
        (selectedPreferences, { preferenceName, optionId }) => {
            if (!selectedPreferenceNames.has(preferenceName)) {
                return selectedPreferences
            }

            if (dinnerSuggestionsMoreOfOptionIds.has(optionId)) {
                return {
                    ...selectedPreferences,
                    moreOf: [
                        ...selectedPreferences.moreOf,
                        optionId as DinnerSuggestionsMoreOfOptionId,
                    ],
                }
            }

            return {
                ...selectedPreferences,
                importantToMe: [
                    ...selectedPreferences.importantToMe,
                    optionId as DinnerSuggestionsImportantToMeOptionId,
                ],
            }
        },
        {
            moreOf: [],
            importantToMe: [],
        },
    )
}

export const fetchDinnerSuggestionsPreferences =
    async (): Promise<DinnerSuggestionsSelectedPreferences> => {
        try {
            const response = await performMiddagstipsBffRequest<
                DinnerSuggestionsPreferenceResponseItem[]
            >({
                path: "/api/recipes/preferences",
                method: "GET",
                query: {
                    chainId: "MENY",
                },
            })

            return parseDinnerSuggestionsPreferencesResponse(response)
        } catch (error) {
            Logger.error("modules/recipes/preferences/fetchDinnerSuggestionsPreferences()", error)
            throw error
        }
    }

export const saveDinnerSuggestionsPreferences = async (
    preferences: DinnerSuggestionsSelectedPreferences,
) => {
    const body = buildDinnerSuggestionsPreferencesRequest(preferences)

    try {
        await performMiddagstipsBffRequest({
            path: "/api/recipes/preferences",
            method: "PUT",
            body,
            query: {
                chainId: "MENY",
            },
        })
    } catch (error) {
        Logger.error("modules/recipes/preferences/saveDinnerSuggestionsPreferences()", error)
        throw error
    }
}
